import React from 'react';
import { Switch } from '@cotera/client/v0/components/ui/switch';
import { useWorkflowModel } from '../../workflow.model';
import { draftSetTriggerBindingEnabled } from '../../workflow-draft-graph';

/**
 * The per-workflow on/off switch on a trigger card.
 *
 * Flips the binding, not the trigger row — see `draftSetTriggerBindingEnabled`
 * for why that distinction matters. Writes to the draft graph like every other
 * canvas edit, which is what puts it on the editor's autosave; it deliberately
 * does not fire its own request.
 *
 * Takes the model directly rather than `useWorkflowGraphMutations`, which pulls
 * in a live `resource.list` watchable for the trigger picker. Every other
 * caller of that hook is a singleton (the canvas root, a drawer); this is the
 * first per-node one, so going through it would mean one list request and one
 * broadcast subscription per trigger card on screen.
 */
export const WorkflowTriggerEnabledToggle: React.FC<{
  bindingId: string;
  /** Whether the trigger fires; owned by `workflowGraphToFlow`. */
  checked: boolean;
}> = ({ bindingId, checked }) => {
  const model = useWorkflowModel();
  const label = checked
    ? 'Trigger is on — switch off to stop it running this workflow'
    : 'Trigger is off — switch on to let it run this workflow';

  return (
    // `nodrag`/`nopan` keep React Flow from treating the switch as a grab
    // handle, and the stopped click keeps flipping it from also selecting the
    // node and opening the inspector behind it.
    <label
      className="nodrag nopan flex shrink-0 cursor-pointer items-center gap-1.5"
      title={label}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {/* Named, not just shown. The rest of a switched-off card is dimmed, so
          without a word next to the switch "off" looks like the card is
          disabled rather than like a control waiting to be flipped back. */}
      <span className="text-[11px] leading-none font-medium text-muted-foreground">
        {checked ? 'On' : 'Off'}
      </span>
      {/* Two thirds of the default switch, which is sized for a settings form.
          `size-3` has to reach the thumb through its slot, since `Switch`
          renders it internally and takes no thumb className. */}
      <Switch
        checked={checked}
        aria-label={label}
        className="h-3.5 w-6 data-[state=unchecked]:bg-muted-foreground/35 [&_[data-slot=switch-thumb]]:size-3"
        onCheckedChange={(enabled) => {
          model.updateDraftState((state) =>
            draftSetTriggerBindingEnabled(state, { bindingId, enabled })
          );
        }}
      />
    </label>
  );
};
